import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  portalDs,
  PortalDsButton,
  PortalDsField,
  PortalDsInput,
  PortalDsSelect,
} from 'src/components/portal-design-system';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { cn } from 'src/lib/utils';
import { useGetCompaniesQuery } from 'src/pages/billing/endpoints/billing.endpoints';
import { canSyncInvoice } from 'src/pages/billing/utils/billing-utils';
import type { ReduxState } from 'src/redux/redux-state';
import AccountTable from '../components/account-table';
import CampaignTable from '../components/campaign-table';
import { ManageAccountsSyncSuccessDialog } from '../components/manage-accounts-sync-success-dialog';
import ManageListPagination from '../components/manage-list-pagination';
import {
  useGetAccountsQuery,
  useGetCampaignsQuery,
} from '../endpoints/accounts.endpoints';
import { useManageAccountsSync } from '../hooks/use-manage-accounts-sync';
import { useManageListUrlParams } from '../hooks/use-manage-list-url-params';

type ManageView = 'accounts' | 'campaigns';

const SEGMENT_TRIGGER_CLASS =
  'rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 text-[14px] font-semibold text-[#5E6278] shadow-none hover:bg-transparent hover:text-[#0D0F1A] [&[aria-selected=true]]:border-[#0D0F1A] [&[aria-selected=true]]:bg-transparent [&[aria-selected=true]]:text-[#0D0F1A] [&[aria-selected=true]]:shadow-none';

function readView(params: URLSearchParams): ManageView {
  return params.get('view') === 'campaigns' ? 'campaigns' : 'accounts';
}

const ManageAccountsCampaignsTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = readView(searchParams);
  const { filter, searchQuery, setFilterWithUrl, setSearchWithUrl } = useManageListUrlParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchDraft, setSearchDraft] = useState(searchQuery);

  const permissionGroups = useSelector(
    (state: ReduxState) => state.userState.permissions?.groups ?? []
  );
  const showSyncButton = canSyncInvoice(permissionGroups);
  const {
    syncAccountsAndCampaigns,
    isSyncAccountsCampaignsPending,
    syncSuccessModalOpen,
    onSyncSuccessModalOpenChange,
  } = useManageAccountsSync();

  const { data: companies = [] } = useGetCompaniesQuery();

  const queryArgs = useMemo(
    () => ({
      page,
      page_size: pageSize,
      search: searchQuery.trim() || undefined,
      attention_needed: filter === 'attention_needed' ? true : undefined,
    }),
    [page, pageSize, searchQuery, filter]
  );

  const {
    data: accountsData,
    isFetching: isAccountsFetching,
    isLoading: isAccountsLoading,
    refetch: refetchAccounts,
  } = useGetAccountsQuery(queryArgs, { skip: activeView !== 'accounts' });

  const {
    data: campaignsData,
    isFetching: isCampaignsFetching,
    isLoading: isCampaignsLoading,
    refetch: refetchCampaigns,
  } = useGetCampaignsQuery(queryArgs, { skip: activeView !== 'campaigns' });

  const accounts = accountsData?.items ?? [];
  const campaigns = campaignsData?.items ?? [];
  const totalObjects =
    activeView === 'accounts'
      ? (accountsData?.total_objects ?? 0)
      : (campaignsData?.total_objects ?? 0);
  const totalPages =
    activeView === 'accounts'
      ? (accountsData?.total_pages ?? 0)
      : (campaignsData?.total_pages ?? 0);
  const isLoading = activeView === 'accounts' ? isAccountsLoading : isCampaignsLoading;
  const isFetching = activeView === 'accounts' ? isAccountsFetching : isCampaignsFetching;
  const attentionCount =
    activeView === 'accounts'
      ? accounts.filter((item) => item.attention_needed).length
      : campaigns.filter((item) => item.attention_needed).length;

  useEffect(() => {
    setSearchDraft(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filter, pageSize, activeView]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchDraft !== searchQuery) {
        setSearchWithUrl(searchDraft);
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [searchDraft, searchQuery, setSearchWithUrl]);

  const setActiveView = (view: ManageView) => {
    const nextParams = new URLSearchParams(searchParams);
    if (view === 'campaigns') {
      nextParams.set('view', 'campaigns');
    } else {
      nextParams.delete('view');
    }
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <ManageAccountsSyncSuccessDialog
        open={syncSuccessModalOpen}
        onOpenChange={onSyncSuccessModalOpenChange}
      />

      <Tabs
        value={activeView}
        onValueChange={(value) => setActiveView(value as ManageView)}
        className="flex h-full flex-col"
      >
        <div className="border-b border-[#EDEEF2] px-6 pt-6 sm:px-10">
          <div className="mb-5">
            <h1 className="text-[20px] font-semibold tracking-[-0.02em] text-[#0D0F1A]">
              Accounts & Campaigns
            </h1>
            <p className="mt-1 text-[14px] text-[#5E6278]">
              Assign billing companies and SKUs for advertiser accounts and campaigns.
            </p>
          </div>

          <TabsList className="h-auto rounded-none bg-transparent p-0">
            <TabsTrigger
              value="accounts"
              className={cn(SEGMENT_TRIGGER_CLASS, 'mr-8')}
            >
              Accounts
            </TabsTrigger>
            <TabsTrigger
              value="campaigns"
              className={SEGMENT_TRIGGER_CLASS}
            >
              Campaigns
            </TabsTrigger>
          </TabsList>
        </div>

        <div className={cn(portalDs.page, 'min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-10')}>
          <div className="mb-6 flex flex-wrap items-end gap-3 rounded-xl border border-[#EDEEF2] bg-white p-4">
            <PortalDsField
              label="Search"
              filter
              className="min-w-[200px] flex-1"
            >
              <PortalDsInput
                value={searchDraft}
                placeholder="Search by title…"
                onChange={(event) => setSearchDraft(event.target.value)}
              />
            </PortalDsField>

            <PortalDsField
              label="Filter"
              filter
              className="min-w-[160px]"
            >
              <PortalDsSelect
                value={filter}
                onChange={(event) =>
                  setFilterWithUrl(event.target.value as 'all' | 'attention_needed')
                }
              >
                <option value="all">
                  All
                </option>
                <option value="attention_needed">
                  Attention needed
                </option>
              </PortalDsSelect>
            </PortalDsField>

            <div className="ml-auto flex flex-wrap items-center gap-3">
              {attentionCount > 0 ? (
                <span className={portalDs.rowCount}>
                  {attentionCount}
                  {' '}
                  needing attention on this page
                </span>
              ) : null}
              {showSyncButton ? (
                <PortalDsButton
                  type="button"
                  variant="primary"
                  size="md"
                  disabled={isSyncAccountsCampaignsPending}
                  onClick={() => {
                    void syncAccountsAndCampaigns();
                  }}
                >
                  {isSyncAccountsCampaignsPending ? (
                    <>
                      <Loader2
                        className="size-4 animate-spin"
                        aria-hidden
                      />
                      Syncing…
                    </>
                  ) : (
                    'Sync Accounts and Campaigns'
                  )}
                </PortalDsButton>
              ) : null}
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-[#5E6278]">
              <Loader2
                className="size-5 animate-spin"
                aria-hidden
              />
              <span>
                {activeView === 'accounts' ? 'Loading accounts…' : 'Loading campaigns…'}
              </span>
            </div>
          ) : (
            <div className="flex min-h-0 flex-col">
              <TabsContent
                value="accounts"
                className="mt-0"
              >
                <AccountTable
                  accounts={accounts}
                  companies={companies}
                  onCompanySelect={() => {
                    void refetchAccounts();
                  }}
                  isRefreshing={isFetching}
                />
              </TabsContent>
              <TabsContent
                value="campaigns"
                className="mt-0"
              >
                <CampaignTable
                  campaigns={campaigns}
                  companies={companies}
                  onUpdateCampaign={() => {
                    void refetchCampaigns();
                  }}
                  isRefreshing={isFetching}
                />
              </TabsContent>

              <ManageListPagination
                page={page}
                totalPages={totalPages}
                totalObjects={totalObjects}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                disabled={isFetching}
              />
            </div>
          )}
        </div>
      </Tabs>
    </div>
  );
};

export default ManageAccountsCampaignsTab;
